import React, { useEffect, useState } from 'react';
import { Typography, Box } from '@mui/material';
import ApiService from '../../services/api-service';
import ImageAdv from '../ImageAdv';
import { getUserId, putAdRecors, hasPremium } from '../../constants/function';
import './index.scss';

/**
 * AdGate — shows an interstitial ad before content loads.
 * Tries direct ad from backend first; if none available, skips to content.
 * AdSense cannot be used here (interstitial ad-only screens violate AdSense policy).
 * Premium users skip entirely.
 */
export default function AdGate({ children, isLivestream = false }) {
  const isPremium = hasPremium();
  const userId = getUserId();

  const [adDismissed, setAdDismissed] = useState(isPremium);
  const [directAd, setDirectAd] = useState(null);
  const [skipSeconds, setSkipSeconds] = useState(5);
  const [loading, setLoading] = useState(!isPremium);

  useEffect(() => {
    if (isPremium) return;
    fetchAd();
  }, []);

  // Skip countdown
  useEffect(() => {
    if (isPremium || adDismissed || loading || skipSeconds <= 0) return;
    const timer = setTimeout(() => setSkipSeconds((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [skipSeconds, adDismissed, loading, isPremium]);

  const fetchAd = async () => {
    try {
      const response = await ApiService.ADD(`&livestream=${isLivestream}`);
      if (response?.result && response.result.length !== 0 && typeof response.result !== 'string') {
        setDirectAd(response.result);
        putAdRecors({
          eventType: 'adView',
          adId: response.result.id,
          userId,
          metadata: { platform: 'web' },
          viewLivestream: isLivestream,
          timestamp: Date.now(),
          orderNo: response.result?.orderNo,
          adType: response.result.adType,
        });
      } else {
        // No direct ad available — skip to content immediately
        // (AdSense cannot be shown on interstitial/ad-only screens per policy)
        setAdDismissed(true);
      }
    } catch {
      // Backend error — skip to content immediately
      setAdDismissed(true);
    }
    setLoading(false);
  };

  // Premium or dismissed — show content
  if (adDismissed) {
    return <>{children}</>;
  }

  if (loading) {
    return (
      <div className="adgate-overlay">
        <Box sx={{ color: '#fff', fontSize: '14px' }}>Loading...</Box>
      </div>
    );
  }

  return (
    <div className="adgate-overlay">
      {/* Skip button — always visible */}
      <div className="adgate-skip">
        <Typography
          onClick={() => {
            if (skipSeconds <= 0) setAdDismissed(true);
          }}
          sx={{
            color: '#fff',
            cursor: skipSeconds <= 0 ? 'pointer' : 'default',
            background: 'rgba(0,0,0,0.7)',
            padding: '8px 16px',
            borderRadius: '4px',
            fontSize: '14px',
            '&:hover': skipSeconds <= 0 ? { background: 'rgba(255,255,255,0.2)' } : {},
          }}
        >
          {skipSeconds > 0 ? `Skip in ${skipSeconds}s` : 'Skip ▸'}
        </Typography>
      </div>

      {/* Direct ad — image */}
      {directAd && directAd.url && directAd.type === 'image' && (
        <div
          className="adgate-content"
          onClick={() => {
            putAdRecors({
              eventType: 'adClick',
              adId: directAd.id,
              userId,
              metadata: { platform: 'web' },
              timestamp: Date.now(),
              orderNo: directAd?.orderNo,
              adType: directAd.adType,
            });
            window.open(directAd.redirectUrl, '_blank');
          }}
        >
          <ImageAdv
            url={directAd.url}
            type={directAd.type}
            href={directAd.redirectUrl || ''}
            time={5}
            onComplete={() => setAdDismissed(true)}
          />
        </div>
      )}

      {/* Direct ad — video */}
      {directAd && directAd.url && directAd.type === 'video' && (
        <div className="adgate-content">
          <video
            src={directAd.url}
            autoPlay
            style={{ maxWidth: '100%', maxHeight: '80vh', borderRadius: '8px' }}
            controlsList="nodownload nofullscreen noremoteplayback"
            disablePictureInPicture
            onEnded={() => setAdDismissed(true)}
          />
        </div>
      )}

    </div>
  );
}
